import React, { useEffect, useState } from 'react';
import { Maximize2, MapPin, Minimize2, Pencil, Plus } from 'lucide-react';
import { Section, Input, Button, IconButton } from '../ui';
import { ZoneSearch, BulkImportResult } from '../ZoneSearch';
import { ZoneTagCloud } from '../ZoneTagCloud';
import { MapComponent } from '../MapComponent';
import { KernzonenField } from '../KernzonenField';
import type { ZoneNode } from '../../utils/zoneUtils';
import { defaultSubscriptionTitle } from '../../utils/subscriptionTitle';

export interface WhereGroupProps {
  density?: 'default' | 'compact';
  name: string;
  onNameChange: (value: string) => void;
  zones: ZoneNode[];
  allSelectedZips: string[];
  maxZipCodes: number;
  zoneSearchDisabled: boolean;
  isLoading: boolean;
  onSelectZone: (zone: ZoneNode) => void;
  onBulkSelectZones: (nodes: ZoneNode[]) => BulkImportResult;
  otherSubscriptionZips?: Set<string>;
  constraintMessage: string | null;
  onZoneRemove: (zoneId: string) => void;
  onZoneExplode: (zoneId: string) => void;
  onResetAll: () => void;
  showKernzonen: boolean;
  effectiveZipOrder: string[];
  onKernChange: (newZips: string[]) => void;
  onAddZip: (zip: string) => void;
  onRemoveZip: (zip: string) => void;
}

/** Name shown as text with a pencil; becomes an input only while editing. */
const NameField: React.FC<{ name: string; placeholder: string; disabled: boolean; onChange: (value: string) => void }> = ({
  name,
  placeholder,
  disabled,
  onChange,
}) => {
  const [editing, setEditing] = useState(false);
  return (
    <div className="flex flex-col gap-1">
      {React.createElement(
        editing ? 'label' : 'span',
        { htmlFor: editing ? 'subscription-name' : undefined, className: 'text-sm font-medium text-ink' },
        'Name ',
        <span className="font-normal text-muted">(optional)</span>
      )}
      {editing ? (
        <Input
          id="subscription-name"
          autoFocus
          value={name}
          onChange={(e) => onChange(e.target.value)}
          onBlur={() => setEditing(false)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === 'Escape') {
              e.preventDefault();
              setEditing(false);
            }
          }}
          placeholder={placeholder}
          disabled={disabled}
        />
      ) : (
        <div className="flex min-h-11 items-center gap-2">
          <span className={name ? 'min-w-0 truncate font-medium text-ink' : 'min-w-0 truncate text-muted'}>
            {name || placeholder}
          </span>
          <IconButton
            label="Name bearbeiten"
            icon={<Pencil className="h-4 w-4" />}
            variant="ghost"
            size="sm"
            onClick={() => setEditing(true)}
            disabled={disabled}
          />
        </div>
      )}
    </div>
  );
};

/** "Wo" group: name, zone search/tags, Kernzone slots and the collapsible map. */
export const WhereGroup: React.FC<WhereGroupProps> = ({
  density = 'default',
  name,
  onNameChange,
  zones,
  allSelectedZips,
  maxZipCodes,
  zoneSearchDisabled,
  isLoading,
  onSelectZone,
  onBulkSelectZones,
  otherSubscriptionZips,
  constraintMessage,
  onZoneRemove,
  onZoneExplode,
  onResetAll,
  showKernzonen,
  effectiveZipOrder,
  onKernChange,
  onAddZip,
  onRemoveZip,
}) => {
  // Page editor and /tg share one layout: a static map preview beside the fields
  // (45% on phones, 35% from lg), expandable to the full map. On phones the preview
  // floats right and the badges flow around it, so no space stays unused.
  const [mapExpanded, setMapExpanded] = useState(false);
  // Desktop page editor: name, search and summary sit beside the map; the badges get the full
  // width below. Phones and /tg keep the badges in the fields column, flowing around the map.
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia?.('(min-width: 1024px)').matches ?? false);
  useEffect(() => {
    const mq = window.matchMedia?.('(min-width: 1024px)');
    if (!mq) return;
    const onChange = () => setIsDesktop(mq.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);
  const tagsBelow = isDesktop;
  // The place search is shown on demand ("+ Ort hinzufügen"), always while nothing is selected,
  // and always on desktop, where it fills the space beside the map.
  const [searchOpen, setSearchOpen] = useState(false);
  // Captured on mount: a new search starts empty and not loading; an edit mounts while loading,
  // so its zones arriving later never trigger the autofocus.
  const [isNewSearch] = useState(() => zones.length === 0 && !isLoading);
  const showSearch = zones.length === 0 || searchOpen || tagsBelow;
  const gap = density === 'compact' ? 'gap-3' : 'gap-4';
  const namePlaceholder = allSelectedZips.length > 0 ? defaultSubscriptionTitle(zones, allSelectedZips) : 'z. B. Altbau Friedrichshain';

  const fullMap = (
    <MapComponent
      selectedZipCodes={allSelectedZips}
      kernZipCodes={showKernzonen ? effectiveZipOrder.slice(0, 3) : undefined}
      onAddZip={onAddZip}
      onRemoveZip={onRemoveZip}
    />
  );

  const tags = (
    <ZoneTagCloud
      zones={zones}
      onRemove={onZoneRemove}
      onExplode={onZoneExplode}
      disabled={isLoading}
      // flow: plain block (not flex) so the badges wrap around the floated map preview.
      flow={!tagsBelow}
      trailing={
        !showSearch && (
          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            disabled={isLoading || zoneSearchDisabled}
            className="inline-flex items-center gap-1 rounded-full border border-dashed border-brand/60 px-3 py-1.5 text-sm font-medium text-brand hover:bg-brand-soft disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Plus className="h-3.5 w-3.5" aria-hidden="true" /> Ort hinzufügen
          </button>
        )
      }
    />
  );

  const summary = (
    <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-sm text-muted">
      <span>
        {zones.length > 0 && `${zones.length} ${zones.length === 1 ? 'Ort' : 'Orte'} · `}
        {allSelectedZips.length} von {maxZipCodes} PLZ
      </span>
      {zones.length > 1 && (
        <button
          type="button"
          onClick={onResetAll}
          disabled={isLoading}
          className="whitespace-nowrap rounded-control px-2 py-0.5 font-medium hover:bg-sunken hover:text-ink disabled:cursor-not-allowed disabled:opacity-50"
        >
          Alle entfernen
        </button>
      )}
    </div>
  );

  // The "Karte vergrößern" preview. Desktop: grid column beside the fields. Phones and /tg:
  // floats right so the badges flow around it into the leftover space.
  const previewButton = (
    <button
      type="button"
      onClick={() => setMapExpanded(true)}
      // isolate: Leaflet's panes use z-index 400+; keep them inside this box so the
      // sticky app header stays on top while scrolling.
      className={
        tagsBelow
          ? 'group relative isolate h-[170px] overflow-hidden rounded-control lg:h-[200px] border border-line text-left shadow-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand'
          : 'group relative isolate float-right ml-3 mb-2 h-[170px] w-[45%] overflow-hidden rounded-control border border-line text-left shadow-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand'
      }
      aria-label="Karte vergrößern"
    >
      <MapComponent selectedZipCodes={allSelectedZips} kernZipCodes={showKernzonen ? effectiveZipOrder.slice(0, 3) : undefined} preview />
      <span className="absolute right-1.5 top-1.5 z-[500] inline-flex items-center gap-1 rounded-control bg-card/95 px-2 py-1 text-xs font-medium text-ink shadow-card group-hover:bg-card">
        <Maximize2 className="h-3.5 w-3.5" aria-hidden="true" /> <span className="hidden sm:inline">Vergrößern</span>
      </span>
    </button>
  );
  const showPreview = !mapExpanded && allSelectedZips.length > 0;

  const fields = tagsBelow ? (
    <div className={`flex min-w-0 flex-col ${gap}`}>
      <NameField name={name} placeholder={namePlaceholder} disabled={isLoading} onChange={onNameChange} />

      <div className="flex flex-col gap-2">
        <span className="text-sm font-medium text-ink">Orte</span>
        {showSearch && (
          <ZoneSearch
            onSelectZone={(zone) => {
              onSelectZone(zone);
              setSearchOpen(false);
            }}
            onBulkSelectZones={onBulkSelectZones}
            disabled={isLoading || zoneSearchDisabled}
            alreadyCoveredZips={otherSubscriptionZips}
            // New search or opened via "+ Ort hinzufügen": start typing right away.
            autoFocus={searchOpen || isNewSearch}
          />
        )}
        {constraintMessage && (
          <p role="alert" className="text-sm text-danger-text">{constraintMessage}</p>
        )}
        {summary}
      </div>
    </div>
  ) : (
    // Plain block (not flex): the floated preview and the badge flow share one
    // formatting context, so pills wrap around the map into the leftover space.
    <div className="min-w-0">
      {showPreview && previewButton}
      <NameField name={name} placeholder={namePlaceholder} disabled={isLoading} onChange={onNameChange} />
      <div className={density === 'compact' ? 'mt-3' : 'mt-4'}>
        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium text-ink">Orte</span>
          {showSearch && (
            <ZoneSearch
              onSelectZone={(zone) => {
                onSelectZone(zone);
                setSearchOpen(false);
              }}
              onBulkSelectZones={onBulkSelectZones}
              disabled={isLoading || zoneSearchDisabled}
              alreadyCoveredZips={otherSubscriptionZips}
              // New search or opened via "+ Ort hinzufügen": start typing right away.
              autoFocus={searchOpen || isNewSearch}
            />
          )}
          {constraintMessage && (
            <p role="alert" className="text-sm text-danger-text">{constraintMessage}</p>
          )}
        </div>
        {tags}
        {summary}
      </div>
    </div>
  );

  // Full width below the fields/map row: its three slot pickers don't fit a narrow column.
  const kernzonen = showKernzonen && (
    <KernzonenField allZips={effectiveZipOrder} onChange={onKernChange} disabled={isLoading} />
  );

  const content = (
    <div className="flex flex-col gap-4">
      {tagsBelow ? (
        <div className={showPreview ? 'grid grid-cols-[minmax(0,1fr)_35%] items-start gap-6' : ''}>
          {fields}
          {showPreview && previewButton}
        </div>
      ) : fields}
      {tagsBelow && tags}
      {kernzonen}
      {mapExpanded && (
        <div className="isolate flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-ink">Karte</span>
            <Button variant="ghost" size="sm" leftIcon={<Minimize2 className="h-4 w-4" aria-hidden="true" />} onClick={() => setMapExpanded(false)}>
              Verkleinern
            </Button>
          </div>
          {fullMap}
        </div>
      )}
    </div>
  );

  if (density === 'compact') {
    // Native Telegram list convention: small muted uppercase section title (allowed only on /tg).
    return (
      <div className="flex flex-col gap-2">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">Wo</h2>
        {content}
      </div>
    );
  }

  return (
    <Section title="Wo" icon={<MapPin className="h-5 w-5" aria-hidden="true" />}>
      {content}
    </Section>
  );
};
